'use client';

import React from 'react';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';
import { IAnalyticsUserInfo, IUserInfo } from '@/types/shared.types';
import { ITeamFilterSelectedItems } from '@/types/teams.types';
import { triggerLoader } from '@/utils/common.utils';

import { createFilterGetter } from '@/services/teams/utils/createFilterGetter';
import Image from 'next/image';
import { useTeamFilterStore, useTeamFilterCount } from '@/services/teams';
import { FiltersSidePanel } from '@/components/common/filters/FiltersSidePanel';
import { FilterSection } from '@/components/common/filters/FilterSection';
import { GenericCheckboxList } from '@/components/common/filters/GenericCheckboxList';
import { FiltersSearch } from '@/components/page/teams/FiltersSearch';
import { FundFilterToggle } from '@/components/core/FundFilterToggle';
import { useTeamAnalytics } from '@/analytics/teams.analytics';
import { FilterCheckSizeInput } from '@/components/page/members/MembersFilter/FilterCheckSizeInput';
import { FilterDivider } from '@/components/page/members/MembersFilter/FilterDivider';
import { InvestmentFocusFilter } from '@/components/page/teams/TeamsFilter/components/InvestmentFocusFilter';
import { TeamsFocusAreaFilter } from './components/TeamsFocusAreaFilter';
import { getPriorityLabel } from '@/utils/team.utils';
import { isTierUser } from '@/utils/user/isTierUser';

import { processCommunityAffiliationsFilterOptions } from './utils/processCommunityAffiliationsFilterOptions';

export interface TeamsFilterProps {
  filterValues: ITeamFilterSelectedItems | undefined;
  userInfo?: IUserInfo;
  searchParams: any;
  onClose?: () => void;
}

/**
 * TeamsFilter - Modern teams filter using generic components
 *
 * Rebuilt using the generic filter infrastructure for consistency with Members filter.
 * Uses shared components: FiltersSidePanel, FilterSection, GenericFilterToggle, etc.
 */
export function TeamsFilter(props: TeamsFilterProps) {
  const { filterValues, userInfo, searchParams, onClose } = props;

  const { clearParams, params } = useTeamFilterStore();
  const appliedFiltersCount = useTeamFilterCount();
  const analytics = useTeamAnalytics();
  const canSearch = userInfo?.rbac?.effectivePermissions.some((p) => p.code === 'team.search.read');
  const canSeeSource = userInfo?.rbac?.effectivePermissions.some((p) => p.code === 'membership.source.read');
  const isDirectoryAdmin = userInfo?.rbac?.effectivePermissions.some((p) => p.code === 'directory.admin.full'); // isAdminUser(userInfo);
  const canSeePriority = userInfo?.rbac?.effectivePermissions.some((p) => p.code === 'team.priority.read');
  const isTierViewer = isDirectoryAdmin || canSearch || isTierUser(userInfo) || canSeePriority;

  // Create data hooks at the top level (not conditionally)
  // These factory functions return data hooks that can be passed to GenericCheckboxList
  const getTeamTags = createFilterGetter(filterValues?.tags);
  const getMembershipSources = createFilterGetter(filterValues?.membershipSources);
  const getCommunityAffiliations = createFilterGetter(filterValues?.communityAffiliations, {
    processFilterItems: processCommunityAffiliationsFilterOptions,
  });
  const getFundingStages = createFilterGetter(filterValues?.fundingStage);
  const getPriorities = createFilterGetter(filterValues?.priorities, {
    formatLabel: (item) => getPriorityLabel(item.value, true),
  });

  // Wrap clearParams to include analytics
  const handleClearParams = () => {
    analytics.onClearAllFiltersClicked(userInfo as IAnalyticsUserInfo);
    clearParams();
  };

  // Wrap onClose to include analytics for "Apply filters" button
  const handleClose = () => {
    analytics.onTeamShowFilterResultClicked();
    if (onClose) {
      onClose();
    }
  };

  // Analytics callbacks for Tags filter
  const handleTagsChange = (key: string, values: string[]) => {
    analytics.onTeamsTagsFilterSelected({ page: 'Teams', tags: values });
  };

  const handleTagsSearch = (searchText: string) => {
    analytics.onTeamsTagsFilterSearched({ page: 'Teams', searchText });
  };

  // Analytics callbacks for Membership Sources filter
  const handleMembershipSourcesChange = (key: string, values: string[]) => {
    analytics.onTeamsMembershipSourcesFilterSelected({ page: 'Teams', membershipSources: values });
  };

  const handleMembershipSourcesSearch = (searchText: string) => {
    analytics.onTeamsMembershipSourcesFilterSearched({ page: 'Teams', searchText });
  };

  // Analytics callbacks for Funding Stage filter
  const handleFundingStageChange = (key: string, values: string[]) => {
    analytics.onTeamsFundingStageFilterSelected({ page: 'Teams', fundingStages: values });
  };

  const handleFundingStageSearch = (searchText: string) => {
    analytics.onTeamsFundingStageFilterSearched({ page: 'Teams', searchText });
  };

  const handlePrioritiesChange = (key: string, values: string[]) => {
    analytics.onTeamsPrioritiesFilterSelected({ page: 'Teams', priorities: values });
  };

  return (
    <FiltersSidePanel onClose={handleClose} clearParams={handleClearParams} appliedFiltersCount={appliedFiltersCount}>
      {canSearch && (
        <FilterSection title="Team Search">
          <FiltersSearch searchParams={searchParams} userInfo={userInfo} />
        </FilterSection>
      )}

      {/* Membership Source */}
      {canSeeSource && !isEmpty(filterValues?.membershipSources) && (
        <FilterSection title="Membership Source">
          <GenericCheckboxList
            label="Search or select membership source"
            paramKey="membershipSources"
            placeholder="E.g. Direct..."
            filterStore={useTeamFilterStore}
            useGetDataHook={getMembershipSources}
            onChange={(key, values) => {
              triggerLoader(true);
              handleMembershipSourcesChange(key, values);
              if (!isEmpty(values)) {
                const latestValue = last(values) || '';
                analytics.onFilterApplied('membershipSources', latestValue);
              }
            }}
            onSearch={handleMembershipSourcesSearch}
          />
        </FilterSection>
      )}

      {/* Community Affiliations */}
      {!isEmpty(filterValues?.communityAffiliations) && (
        <FilterSection title="Community Affiliations">
          <GenericCheckboxList
            label="Search or select community affiliations"
            paramKey="communityAffiliations"
            placeholder="E.g. FIL Dev..."
            filterStore={useTeamFilterStore}
            useGetDataHook={getCommunityAffiliations}
            onChange={(key, values) => {
              triggerLoader(true);
            }}
          />
        </FilterSection>
      )}

      {isTierViewer && (
        <FilterSection
          title="Priority"
          titleIcon={<Image src="/icons/stack-blue.svg" alt="stack" width={18} height={20} />}
        >
          <GenericCheckboxList
            hint={
              <div>
                <ul style={{ listStyleType: 'disc', paddingLeft: '16px' }}>
                  <li>PL network team prioritization</li>
                  <li>Lower number = higher importance level</li>
                </ul>
              </div>
            }
            paramKey="priorities"
            placeholder="E.g. Priority 1, Priority 2..."
            filterStore={useTeamFilterStore}
            useGetDataHook={getPriorities}
            defaultItemsToShow={6}
            hideSearch
            onChange={(key, values) => {
              triggerLoader(true);
              handlePrioritiesChange(key, values);
            }}
          />
        </FilterSection>
      )}

      {/* Focus Area */}
      {filterValues?.focusAreas && (
        <TeamsFocusAreaFilter focusAreas={filterValues.focusAreas} searchParams={searchParams} />
      )}

      {/* Tags (Industry Tags) */}
      {!isEmpty(filterValues?.tags) && (
        <FilterSection title="Tags">
          <GenericCheckboxList
            label="Search or select industry tags"
            paramKey="tags"
            placeholder="E.g. AI, DeSci, Neurotech"
            filterStore={useTeamFilterStore}
            useGetDataHook={getTeamTags}
            defaultItemsToShow={5}
            onChange={(key, values) => {
              triggerLoader(true);
              handleTagsChange(key, values);
              if (!isEmpty(values)) {
                const latestValue = last(values) || '';
                analytics.onFilterApplied('tags', latestValue);
              }
            }}
            onSearch={handleTagsSearch}
          />
        </FilterSection>
      )}

      {/* Company Stage */}
      {filterValues?.fundingStage && filterValues.fundingStage.length > 0 && (
        <FilterSection title="Company Stage">
          <GenericCheckboxList
            paramKey="fundingStage"
            placeholder="E.g. Seed, Series A..."
            filterStore={useTeamFilterStore}
            useGetDataHook={getFundingStages}
            defaultItemsToShow={10}
            onChange={(key, values) => {
              triggerLoader(true);
              handleFundingStageChange(key, values);
              if (!isEmpty(values)) {
                const latestValue = last(values) || '';
                analytics.onFilterApplied('fundingStage', latestValue);
              }
            }}
            onSearch={handleFundingStageSearch}
            hideSearch
          />
        </FilterSection>
      )}

      {/* Typical Check Size & Investment Focus */}
      <FilterSection title="Investment Funds">
        <FundFilterToggle label="Show all funds" paramKey="isFund" />

        <FilterDivider />

        <FilterCheckSizeInput
          label="Typical Check Size"
          minParamName="minTypicalCheckSize"
          maxParamName="maxTypicalCheckSize"
          filterStore={useTeamFilterStore}
          allowedRange={{
            min: 0,
            max: 5000000,
          }}
          disabled={!params.get('isFund')}
          onChange={() => {
            triggerLoader(true);
          }}
        />

        <FilterDivider />

        <InvestmentFocusFilter />
      </FilterSection>
    </FiltersSidePanel>
  );
}
